import { Avatar, Button, Chip, ListItem, ListItemAvatar, ListItemButton, ListItemText } from "@mui/material";
import { DRAWER_MAX_SELECTION_COUNT } from "app/appConstants";
import { AxiosResponse, GenericAbortSignal } from "axios";
import { MprcSpinnerIndicator } from "components";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { UserSelectionEntityType, UserSelectionEntry, UserSelectorResponse, UserSelectorResponsePaginatedList } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ListChildComponentProps } from "react-window";
import { GenericListDrawer, GenericListFilter, GenericListItemsType } from "views/common/GenericListDrawer";

////////////////////////////////////////////////////////////////////////////////

type ItemData = GenericListItemsType<UserSelectorResponse>;

////////////////////////////////////////////////////////////////////////////////

export type SelectorUsersFilter = {
    searchText: string;
    pageNumber: number;
    pageSize: number;
    ignoreSelectionHistory: boolean;
};

export type UserSelectorDrawerResult = {
    userId: string;
    name: string;
    thumbnailId?: string | null;
};

interface IProps {
    title?: string;
    subtitle?: string;
    multi?: boolean;
    addSelectedButtonText?: string;
    ignoreSelectionHistory?: boolean;
    onClose: (group: UserSelectorDrawerResult[] | undefined) => void;
    onGetData?: (filter: SelectorUsersFilter, signal: GenericAbortSignal | undefined) => Promise<AxiosResponse<UserSelectorResponsePaginatedList>>;
}

/////////////////////////////////////////////////////////////////////////////////

export const UserSelectorDrawer = (props: IProps) => {

    const api = useCoreApi();
    const session = useSession();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const { title, subtitle, multi = false, addSelectedButtonText, onGetData, onClose } = props;

    const [saveSelection, setSaveSelection] = useState<UserSelectorResponse[] | undefined>(undefined);
    const [selected, setSelection] = useState<UserSelectorResponse[]>([]);

    ////////////////////////////////////////////////////////////////////////////////

    const handleGetData = (genericFilter: GenericListFilter, signal: GenericAbortSignal | undefined): Promise<UserSelectorResponsePaginatedList> => {
        return new Promise((resolve, reject) => {
            if (onGetData) {
                onGetData({
                    ...genericFilter,
                    ignoreSelectionHistory: genericFilter.ignoreSelectionHistory ?? false,
                }, signal)
                    .then((response) => resolve(response.data))
                    .catch(reject);
            }
            else {
                api.v1.selectors.users({
                    ...genericFilter,
                    ignoreSelectionHistory: genericFilter.ignoreSelectionHistory ?? false,
                    groupTypes: null
                }, { signal })
                    .then((response) => resolve(response.data))
                    .catch(reject);
            }
        });
    };

    /**
     * Renderiza uma linha da Lista.
     * @param props
     */
    function renderRow(props: ListChildComponentProps<ItemData>) {
        const { index, style } = props;
        const { items, onViewerClick } = props.data;

        const item = items[index];
        if (item == undefined) return <></>;

        const hasDivider = index > 0 && items[index].fromHistory === false && items[index - 1].fromHistory === true;

        return (
            <div key={index} style={style}>
                <ListItem disablePadding disableGutters>
                    <ListItemButton disableGutters onClick={() => onViewerClick && onViewerClick(item)}
                        sx={(theme) => ({
                            marginTop: hasDivider ? "4px" : undefined,
                            paddingTop: hasDivider ? "4px" : theme.spacing(1),
                            borderTop: hasDivider ? "solid 2px" : undefined,
                            borderTopColor: hasDivider ? theme.palette.divider : undefined
                        })}
                    >
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                            <Avatar
                                src={item.thumbnailUrl}
                                sx={(theme) => ({ width: 32, height: 32, backgroundColor: theme.palette.grey[200] })}
                            />
                        </ListItemAvatar >
                        <ListItemText
                            primary={item.name}
                            title={item.name}
                            slotProps={
                                {
                                    primary: { noWrap: true, variant: "h6" }
                                }
                            }
                        />
                    </ListItemButton>
                </ListItem>
            </div>
        );
    }

    // Ao selecionar um item da lista.
    const onItemClick = (item: UserSelectorResponse) => {
        if (!multi)
            setSaveSelection([item]);
        else {
            if (selected.find(p => p.userId == item.userId) == null) {
                if (selected.length == DRAWER_MAX_SELECTION_COUNT)
                    enqueueSnackbar(t("User.UserSelectorDrawer.SelectionLimitedMessage", { count: DRAWER_MAX_SELECTION_COUNT }), { variant: "warning" });
                else
                    setSelection([...selected, item]);
            }
            else {
                enqueueSnackbar(t("User.UserSelectorDrawer.AlreadySelectedMessage", { name: item.name }), { variant: "error" });
            }
        }
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////////

    /// Salva itens selecionados e retorna.
    useEffect(() => {
        if (saveSelection != undefined) {
            var payload = {
                userId: session.user.userId,
                entries: saveSelection.filter(p => !p.fromHistory).map((v) => ({
                    entityType: UserSelectionEntityType.User,
                    entityId: v.userId,
                    otherInfo: ""
                } as UserSelectionEntry))
            };

            if (payload.entries.length > 0) {
                api.v1.user.saveSelectionHistory(payload)
                    .then(() => {
                        onClose(saveSelection);
                    })
                    .catch((e) => {
                        setSaveSelection(undefined);
                        MprcRequestSnackbarErrorReport(e);
                    });
            }
            else {
                onClose(saveSelection);
            }
        }
    }, [saveSelection]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <GenericListDrawer
            title={title || t("User.UserSelectorDrawer.Title")}
            subtitle={subtitle}
            itemHeight={40}
            onClose={() => onClose(undefined)}
            onGetData={handleGetData}
            onViewerClick={onItemClick}
            emptyDataMessage={t("User.UserSelectorDrawer.FilterEmptyMessage")}
            emptyFilterMessage={t("User.UserSelectorDrawer.NoRecordsFoundText")}
            renderRow={renderRow}
            selection="history"
            footer={
                <div style={{ width: "100%" }}>
                    {
                        multi && selected.length > 0 &&
                        <>
                            <div style={{ maxHeight: 200, overflow: "auto" }}>
                                <p><small>{t("User.UserSelectorDrawer.SelectedText")}:</small></p>
                                {
                                    selected.map((item, index) => (
                                        <Chip
                                            key={index}
                                            label={item.name}
                                            size="small"
                                            sx={{ mb: 0.5, mr: 0.5 }}
                                            avatar={<Avatar src={item.thumbnailUrl} />}
                                            onDelete={() =>
                                                setSelection(selected.filter(p => p.userId != item.userId))
                                            }
                                        />
                                    ))
                                }
                            </div>
                            <hr />
                            <div style={{ textAlign: "center" }}>
                                <Button
                                    variant="contained"
                                    disabled={saveSelection != undefined}
                                    endIcon={saveSelection == undefined ? undefined : <MprcSpinnerIndicator />}
                                    onClick={() => setSaveSelection(selected)}
                                >
                                    {addSelectedButtonText || t("User.UserSelectorDrawer.AddSelectedButtonText")}
                                </Button >
                            </div>
                        </>
                    }
                </div>
            }
        />
    );
};
